import { useState } from 'react';
import { ConfigService } from '@/services/config';
import { formatDate } from '@/components/photovogue/moderation-actions';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@repo/ui/components/ui/avatar';
import { Card, CardContent } from '@repo/ui/components/ui/card';
import { Button } from '@repo/ui/components/ui/button';
import { cn } from '@repo/ui/lib/utils';
import { Separator } from '@repo/ui/components/ui/separator';
import { Skeleton } from '@repo/ui/components/ui/skeleton';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@repo/ui/components/ui/tabs';
import { Star } from 'lucide-react';
import {
  PHOTOGRAPHER_MODERATION_STATES,
  PHOTOGRAPHER_STATE_LABELS,
  type Photo,
  type Photographer,
  type PhotographerState,
} from '@/services/photovogue';
import {
  PhotographerStateBadge,
  TierBadge,
} from '@/components/photovogue/state-badge/component';
import { PhotoGrid } from '@/components/photovogue/photo-grid/component';
import { PhotoLightbox } from '@/components/photovogue/photo-lightbox/component';
import { initials } from '@/components/photovogue/photographer-table/component';

export interface PhotographerDetailViewProps {
  photographer: Photographer;
  photos: Photo[];
  photosLoading: boolean;
  updating: boolean;
  onStateChange: (state: PhotographerState) => void;
  onSpotlightChange: (spotlight: boolean) => void;
}

const Stat = ({ label, value }: { label: string; value: number }) => (
  <div className="text-center">
    <p className="text-lg font-semibold">{value}</p>
    <p className="text-xs text-cn-muted-foreground">{label}</p>
  </div>
);

export const PhotographerDetailView = ({
  photographer,
  photos,
  photosLoading,
  updating,
  onStateChange,
  onSpotlightChange,
}: PhotographerDetailViewProps) => {
  const [lightboxPhoto, setLightboxPhoto] = useState<Photo | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const createdAt = photographer.createdAt;
  const hasCreationDate = Boolean(
    createdAt && !Number.isNaN(Date.parse(createdAt)),
  );
  const publicProfileUrl = `https://${ConfigService.isLocalEnv ? 'stag' : 'www'}.vogue.com/photovogue/photographers/${encodeURIComponent(photographer.id)}`;

  const openPhoto = (photo: Photo) => {
    setLightboxPhoto(photo);
    setLightboxOpen(true);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center">
          <Avatar className="h-16 w-16">
            <AvatarImage src={photographer.avatar} alt={photographer.name} />
            <AvatarFallback>{initials(photographer.name)}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-semibold">{photographer.name}</h1>
              <TierBadge tier={photographer.tier} />
              <PhotographerStateBadge state={photographer.state} />
              <Button
                type="button"
                size="sm"
                variant={photographer.tier === 'gold' ? 'secondary' : 'outline'}
                aria-pressed={photographer.tier === 'gold'}
                disabled={updating}
                className={cn(
                  'gap-1.5',
                  photographer.tier === 'gold' &&
                    'border-yellow-300 bg-yellow-50 text-yellow-800 hover:bg-yellow-100',
                )}
                onClick={() => onSpotlightChange(photographer.tier !== 'gold')}
              >
                <Star
                  className="h-3.5 w-3.5"
                  fill={photographer.tier === 'gold' ? 'currentColor' : 'none'}
                />
                {photographer.tier === 'gold'
                  ? 'Remove spotlight'
                  : 'Add to spotlight'}
              </Button>
              <select
                aria-label="Photographer moderation state"
                value={
                  photographer.state === 'to_fix' ? '' : photographer.state
                }
                disabled={updating}
                onChange={(event) =>
                  onStateChange(event.target.value as PhotographerState)
                }
                className="rounded-md border bg-white px-2 py-1 text-xs disabled:opacity-50"
              >
                {photographer.state === 'to_fix' && (
                  <option value="" disabled hidden>
                    Change status
                  </option>
                )}
                {PHOTOGRAPHER_MODERATION_STATES.map((state) => (
                  <option key={state} value={state}>
                    {PHOTOGRAPHER_STATE_LABELS[state]}
                  </option>
                ))}
              </select>
            </div>
            <p className="text-sm text-cn-muted-foreground">
              {photographer.city}, {photographer.country}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              <a
                href={publicProfileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium underline underline-offset-4"
              >
                View public profile
              </a>
            </div>
          </div>
          <div className="flex gap-6">
            <Stat label="Photos" value={photographer.totalPhotos} />
            <Stat label="Best" value={photographer.totalBest} />
            <Stat label="Daily" value={photographer.totalDaily} />
            <Stat label="Warnings" value={photographer.warningCount} />
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="portfolio">
        <TabsList>
          <TabsTrigger value="portfolio">Portfolio</TabsTrigger>
          <TabsTrigger value="details">Details</TabsTrigger>
        </TabsList>

        <TabsContent value="portfolio">
          {photosLoading ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5">
              {Array.from({ length: 8 }).map((_, index) => (
                <Skeleton
                  key={index}
                  className="aspect-square w-full rounded-lg"
                />
              ))}
            </div>
          ) : photos.length === 0 ? (
            <div className="rounded-lg border border-dashed bg-white py-12 text-center text-sm text-cn-muted-foreground">
              No photos yet.
            </div>
          ) : (
            <PhotoGrid
              photos={photos}
              view="grid"
              selectable={false}
              selectedIds={[]}
              onToggleSelect={() => undefined}
              onOpen={openPhoto}
            />
          )}
        </TabsContent>

        <TabsContent value="details">
          <Card>
            <CardContent className="space-y-3 p-6 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-cn-muted-foreground">
                  Account created
                </span>
                <span className="text-right">
                  {hasCreationDate ? (
                    <time dateTime={createdAt!}>{formatDate(createdAt!)}</time>
                  ) : (
                    'Unavailable'
                  )}
                </span>
              </div>
              <Separator />
              {[
                [
                  'Email',
                  photographer.contactStatus === 'unavailable'
                    ? 'Unavailable — please retry loading the profile'
                    : photographer.email,
                ],
                [
                  'Contact',
                  photographer.contactStatus === 'unavailable'
                    ? 'Unavailable — please retry loading the profile'
                    : photographer.contact,
                ],
                ['Photo typology', photographer.categories?.join(', ')],
                ['Photo theme', photographer.themes?.join(', ')],
                ['Agent', photographer.agents?.join(', ')],
              ].map(([label, value]) => (
                <div key={label} className="space-y-3">
                  <div className="flex justify-between gap-4">
                    <span className="text-cn-muted-foreground">{label}</span>
                    <span className="max-w-md break-words text-right">
                      {value || '—'}
                    </span>
                  </div>
                  <Separator />
                </div>
              ))}
              <div className="flex justify-between gap-4">
                <span className="text-cn-muted-foreground">Biography</span>
                <span className="max-w-md whitespace-pre-wrap break-words text-right">
                  {photographer.biography?.trim() || '—'}
                </span>
              </div>
              <Separator />
              <div className="flex justify-between gap-4">
                <span className="text-cn-muted-foreground">Websites</span>
                <span className="text-right">
                  {photographer.websites?.join(', ') || '—'}
                </span>
              </div>
              <Separator />
              <div className="flex justify-between gap-4">
                <span className="text-cn-muted-foreground">
                  Warnings / Bans
                </span>
                <span className="text-right">
                  {photographer.warningCount} / {photographer.banCount ?? '—'}
                </span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <PhotoLightbox
        photo={lightboxPhoto}
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
      />
    </div>
  );
};

export default PhotographerDetailView;
